import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check } from "lucide-react";

/**
 * SignaturePad: canvas HTML5 per catturare la firma manoscritta del medico.
 * Ritorna via onChange(dataUrl|null) — dataUrl è PNG base64.
 */
export default function SignaturePad({ value, onChange, height = 140 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    // Se c'è un valore iniziale, ridisegnalo
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, height);
        setEmpty(false);
      };
      img.src = value;
    }
  }, [height, value]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pointFromEvent(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    setEmpty(false);
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    onChange && onChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange && onChange(null);
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-white overflow-hidden" data-testid="signature-pad">
      <div className="relative" style={{ height }}>
        <canvas ref={canvasRef}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className="w-full block cursor-crosshair touch-none"
                style={{ height }}
                data-testid="signature-canvas" />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm italic">
            Firma qui con dito o mouse
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
        <span className="text-[11px] text-slate-500">
          {empty ? "Nessuna firma" : <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" /> Firma acquisita</span>}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={clear}
                disabled={empty}
                data-testid="signature-clear"
                className="h-7 border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40">
          <Eraser className="h-3 w-3 mr-1" /> Cancella
        </Button>
      </div>
    </div>
  );
}
